/**
 * Documentation
 *
 * mui theme override options: https://mui.com/material-ui/customization/default-theme/
 * mui dark mode: https://mui.com/material-ui/customization/dark-mode/
 * mui colors: https://mui.com/material-ui/customization/color/
 *
 */

import type { PaperOwnProps, Theme, ThemeOptions } from '@mui/material';
import { amber, green, grey, lightBlue, red, teal } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';

import type { BackgroundKeys } from './types/theme';

const fontFamily = '"Roboto", sans-serif';

type ButtonProps = {
  theme: Theme;
  ownerState?: {
    color: 'primary' | 'secondary';
  };
};

type ChipProps = {
  theme: Theme;
  ownerState?: {
    color: 'primary' | 'secondary';
  };
};

const defaultTheme: ThemeOptions = {
  components: {
    MuiAutocomplete: {
      defaultProps: {
        // Match the Paper elevation Select/Menu dropdowns use so the popup
        // background resolves the same way as other dropdowns in the app.
        slotProps: {
          paper: {
            elevation: 8
          }
        }
      },
      styleOverrides: {
        root: {
          '.MuiAutocomplete-inputRoot': {
            padding: '0 5px'
          },
          '.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            paddingTop: '.25rem',
            paddingBottom: '.25rem',
            minHeight: '2rem'
          },
          '.MuiOutlinedInput-root': {
            minHeight: '2.5rem',
            padding: '0.5rem 0.5rem',
            gap: '0.25rem'
          },
          '.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--mui-palette-primary-main)'
          },
          '.MuiAutocomplete-tag': {
            margin: '0'
          },
          '.MuiAutocomplete-input': {
            padding: '0 0.25rem !important'
          }
        },
        option: {
          '&[aria-selected="true"]': {
            backgroundColor: 'var(--mui-palette-primary-main) !important',
            color: 'var(--mui-palette-primary-contrastText)',
            '.MuiTypography-root': {
              color: 'var(--mui-palette-primary-contrastText)'
            }
          }
        }
      }
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          '.MuiButtonGroup-middleButton:hover': {
            borderRightColor: 'transparent'
          }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      variants: [
        {
          props: { variant: 'twoTone' },
          style: ({ theme, ownerState }: ButtonProps) => {
            return {
              backgroundColor:
                ownerState?.color === 'secondary'
                  ? `${theme.vars.palette.secondary.light}`
                  : `rgb(from ${theme.vars.palette[ownerState?.color as 'primary'].main} r g b / 15%)`,
              color:
                ownerState?.color === 'secondary'
                  ? theme.vars.palette.text.primary
                  : theme.vars.palette[ownerState?.color as 'primary'].light
            };
          }
        }
      ],
      styleOverrides: {
        root: {
          lineHeight: '1.25rem',
          minWidth: '2rem',
          textTransform: 'initial' as const,
          variants: [
            {
              props: { variant: 'outlined', color: 'primary' },
              style: ({ theme }: { theme: Theme }) => ({
                color: theme.vars.palette.primary.main
              })
            },
            {
              props: { variant: 'outlined', color: 'secondary' },
              style: ({ theme }: { theme: Theme }) => ({
                color: theme.vars.palette.secondary.contrastText,
                borderColor: theme.vars.palette.border.main,
                '&:hover': {
                  borderColor: theme.vars.palette.border.main,
                  backgroundColor: theme.vars.palette.secondary.light
                }
              })
            },
            {
              props: { variant: 'text', color: 'primary' },
              style: ({ theme }: { theme: Theme }) => ({
                color: theme.vars.palette.primary.main
              })
            },
            {
              props: { variant: 'text', color: 'secondary' },
              style: ({ theme }: { theme: Theme }) => ({
                color: theme.vars.palette.text.primary
              })
            }
          ]
        },
        startIcon: {
          '>*:nth-of-type(1)': {
            fontSize: '1rem'
          }
        },
        endIcon: {
          '>*:nth-of-type(1)': {
            fontSize: '1rem'
          }
        },
        sizeMedium: {
          minHeight: '2rem'
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: '1rem'
          }
        }
      }
    },
    MuiChip: {
      variants: [
        {
          props: { variant: 'twoTone' },
          style: ({ theme, ownerState }: ChipProps) => {
            return {
              border:
                ownerState?.color === 'secondary'
                  ? `solid 1px ${theme.vars.palette.transparent.a25}`
                  : `solid 1px rgb(from ${theme.vars.palette[ownerState?.color as 'primary'].main} r g b / 50%)`,
              backgroundColor:
                ownerState?.color === 'secondary'
                  ? theme.vars.palette.transparent.a10
                  : `rgb(from ${theme.vars.palette[ownerState?.color as 'primary'].main} r g b / 10%)`,
              color:
                ownerState?.color === 'secondary'
                  ? theme.vars.palette.text.primary
                  : theme.vars.palette[ownerState?.color as 'primary'].main
            };
          }
        }
      ],
      styleOverrides: {
        root: {
          borderRadius: '0.25rem',
          variants: [
            {
              props: { variant: 'outlined', color: 'secondary' },
              style: ({ theme }: { theme: Theme }) => ({
                color: theme.vars.palette.text.primary,
                borderColor: theme.vars.palette.border.main
              })
            }
          ]
        },
        sizeSmall: {
          height: '1.25rem'
        },
        sizeMedium: {
          height: '1.5rem',
          lineHeight: '1.5rem'
        },
        label: {
          paddingLeft: '0.5rem',
          paddingRight: '0.5rem'
        },
        deleteIcon: {
          fontSize: '1rem',
          color: 'rgba(var(--mui-palette-text-primaryChannel) / 0.50)'
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          ':not(.MuiInputBase-multiline, .MuiAutocomplete-inputRoot)': {
            height: '2.5rem'
          },
          '&.MuiInputBase-multiline': {
            padding: '0.625rem'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: 'var(--mui-palette-transparent-a5)'
          },
          '.MuiOutlinedInput-input': {
            padding: '0.625rem'
          },
          'textarea.MuiOutlinedInput-input': {
            padding: '0'
          }
        }
      }
    },
    MuiPaper: {
      defaultProps: {
        elevation: 2
      },
      styleOverrides: {
        root: ({
          theme,
          ownerState
        }: {
          theme: Theme;
          ownerState: PaperOwnProps;
        }) => {
          return {
            backgroundImage: 'none',
            backgroundColor:
              theme.vars.palette.background[
                `elevation${ownerState.elevation}` as BackgroundKeys
              ]
          };
        }
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({ theme }) => ({
          lineHeight: theme.spacing(5),
          fontSize: theme.typography.fontSize,
          backgroundColor: theme.vars.palette.transparent.a10
        })
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingTop: theme.spacing(0.75),
          paddingBottom: theme.spacing(0.75)
        })
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '2rem',
          '.svg-inline--fa': {
            fontSize: '1rem',
            opacity: 0.5
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minWidth: '1.52rem !important',
          display: 'flex',
          '.MuiListItemIcon-root': {
            minWidth: '2rem'
          }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 0,
          margin: '0 0.563rem 0 0'
        }
      }
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          padding: '0.35rem 0',
          '.MuiSvgIcon-root': {
            height: '28px',
            width: '28px'
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'var(--mui-palette-text-primary)',
          fontWeight: 'bold'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem'
        },
        head: {
          '&:not(.MuiTableCell-sizeSmall)': {
            minHeight: '3rem',
            height: '3rem'
          }
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          minHeight: '3rem !important'
        }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderRadius: '0',
          border: 'none'
        },
        columnHeader: {
          padding: '0 1rem'
        },
        cell: {
          padding: '0 1rem'
        },
        overlay: {
          color: 'var(--mui-palette-text-secondary)'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          fontSize: '0.875rem'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.elevation1,
          padding: theme.spacing(1.5, 2),
          borderBottom: `1px solid ${theme.vars.palette.border.light}`
        })
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(1.5, 2),
          backgroundColor: theme.vars.palette.background.elevation2
        })
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.elevation1,
          borderTop: `1px solid ${theme.vars.palette.border.light}`,
          margin: '0 !important',
          padding: theme.spacing(1.5, 2)
        })
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiDialogContentText-root': {
            color: 'var(--mui-palette-text-primary)'
          }
        }
      }
    },
    MuiPickersSectionList: {
      styleOverrides: {
        root: {
          padding: '0.625rem 0 !important'
        }
      }
    }
  },
  typography: {
    fontFamily,
    h1: {
      fontSize: '2rem'
    },
    h2: {
      fontFamily,
      fontSize: '1.75rem'
    },
    h3: {
      fontFamily,
      fontSize: '1.5rem'
    },
    h4: {
      fontFamily,
      fontSize: '1.25rem'
    },
    h5: {
      fontFamily,
      fontSize: '1.125rem',
      fontWeight: 700
    },
    h6: {
      fontFamily,
      fontSize: '1rem',
      fontWeight: 700
    },
    body1: {
      fontSize: '0.875rem'
    },
    body2: {
      fontSize: '0.75rem'
    }
  },
  spacing: (...params: number[]) =>
    params.map((param) => `${param * 0.5}rem`).join(' ')
};

const lightTheme = {
  ...defaultTheme,
  palette: {
    primary: {
      main: teal[700],
      light: teal[600],
      dark: teal[800]
    },
    secondary: {
      main: grey[300],
      light: grey[200],
      dark: grey[500]
    },
    unknown: {
      main: '#ACACAC',
      light: grey[600],
      dark: grey[800]
    },
    new: {
      main: '#5C5C5C',
      light: grey[400],
      dark: grey[600]
    },
    disabled: {
      main: '#C7C7C7',
      light: grey[200],
      dark: grey[500]
    },
    warning: {
      main: '#d97706',
      light: '#f59e0b',
      dark: '#b45309'
    },
    background: {
      default: '#e0e0e0',
      elevation0: '#e0e0e0',
      elevation0_a25: 'rgba(224, 224, 224, 0.25)',
      elevation1: '#fafafa',
      elevation2: '#ffffff',
      elevation3: '#d5d7d8',
      navigation: '#101526'
    },
    border: {
      main: 'rgba(0, 0, 0, 0.3)',
      light: 'rgba(0, 0, 0, 0.12)',
      dark: 'rgba(0, 0, 0, 0.42)'
    },
    chartColors: {
      0: '#95CAFF',
      1: '#FFB74D',
      2: '#EF9A9A',
      3: '#BDBDBD',
      4: '#E3EF9A',
      5: '#9AEFBC',
      6: '#A19AEF',
      7: '#DE9AEF',
      8: '#EF9AC3',
      9: '#9AEFDB'
    },
    status: {
      draft: grey[300],
      'needs-review': amber[300],
      open: red[300],
      acknowledged: lightBlue[300],
      completed: green[300],
      remediated: green[500],
      'auto-closed': green[600]
    },
    text: {
      contrast: '#ffffff',
      contrast_a50: 'rgba(255, 255, 255, 0.5)',
      contrast_a75: 'rgba(255, 255, 255, 0.75)',
      primary_a50: 'rgba(0, 0, 0, 0.5)',
      primary_a75: 'rgba(0, 0, 0, 0.75)',
      secondary: grey[600]
    },
    transparent: {
      a5: 'rgba(0, 0, 0, 0.05)',
      a10: 'rgba(0, 0, 0, 0.1)',
      a25: 'rgba(0, 0, 0, 0.25)',
      a50: 'rgba(0, 0, 0, 0.5)',
      a75: 'rgba(0, 0, 0, 0.75)'
    }
  }
};

const darkTheme = {
  ...defaultTheme,
  palette: {
    primary: {
      main: teal[300],
      light: teal[100],
      dark: teal[600]
    },
    secondary: {
      main: grey[700],
      light: grey[600],
      dark: grey[800]
    },
    unknown: {
      main: '#ACACAC',
      light: grey[600],
      dark: grey[800]
    },
    new: {
      main: '#C7C7C7',
      light: grey[400],
      dark: grey[600]
    },
    disabled: {
      main: '#5C5C5C',
      light: grey[200],
      dark: grey[500]
    },

    warning: {
      main: '#d97706',
      light: '#f59e0b',
      dark: '#b45309',
      contrastText: '#1e2631'
    },
    error: {
      main: red[400],
      light: red[300],
      dark: red[500],
      contrastText: '#1e2631'
    },
    background: {
      default: '#1e2631',
      elevation0: '#1e2631',
      elevation0_a25: 'rgba(30, 38, 49, 0.25)',
      elevation1: '#282f3c',
      elevation2: '#2c3442',
      elevation3: '#49505e',
      navigation: '#0F1116'
    },
    border: {
      main: 'rgba(255, 255, 255, 0.3)',
      light: 'rgba(255, 255, 255, 0.12)',
      dark: 'rgba(255, 255, 255, 0.42)'
    },
    chartColors: {
      0: '#95CAFF',
      1: '#FFB74D',
      2: '#EF9A9A',
      3: '#BDBDBD',
      4: '#E3EF9A',
      5: '#9AEFBC',
      6: '#A19AEF',
      7: '#DE9AEF',
      8: '#EF9AC3',
      9: '#9AEFDB'
    },
    status: {
      draft: grey[600],
      'needs-review': amber[800],
      open: red[800],
      acknowledged: lightBlue[800],
      completed: green[800],
      remediated: green[500],
      'auto-closed': green[300]
    },
    text: {
      contrast: '#000000',
      contrast_a50: 'rgba(0, 0, 0, 0.5)',
      contrast_a75: 'rgba(0, 0, 0, 0.75)',
      primary_a50: 'rgba(255, 255, 255, 0.5)',
      primary_a75: 'rgba(0, 0, 0, 0.75)',
      secondary: grey[500]
    },
    transparent: {
      a5: 'rgba(255, 255, 255, 0.05)',
      a10: 'rgba(255, 255, 255, 0.1)',
      a25: 'rgba(255, 255, 255, 0.25)',
      a50: 'rgba(255, 255, 255, 0.5)',
      a75: 'rgba(255, 255, 255, 0.75)'
    }
  },
  components: {
    ...defaultTheme.components
  }
};

const theme = createTheme({
  // breakpoints must be added here to override default theme
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1500,
      xxl: 1920,
      xxxl: 2200
    }
  },
  colorSchemes: {
    light: lightTheme,
    dark: darkTheme
  },
  cssVariables: {
    colorSchemeSelector: 'data-mui-color-scheme'
  }
});

export default theme;
